import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  alpha,
  ButtonBase,
} from "@mui/material";
import React from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";

const reportCards = [
  {
    id: "financial",
    title: "Financial Reports",
    description:
      "Analyse revenue, sales performance, payment summaries, and overall financial health of your restaurant",
    icon: <AttachMoneyIcon sx={{ fontSize: 64 }} />,
    path: "/reports/financial",
    color: "#10b981",
    features: [
      "Revenue & Sales Overview",
      "Daily / Weekly / Monthly Trends",
      "Payment Method Breakdown",
      "Top Selling Items",
    ],
  },
  {
    id: "stocks",
    title: "Stock Reports",
    description:
      "Monitor inventory levels, track raw material usage, identify low-stock items, and manage wastage",
    icon: <InventoryIcon sx={{ fontSize: 64 }} />,
    path: "/reports/stocks",
    color: "#6366f1",
    features: [
      "Current Stock Levels",
      "Low Stock Alerts",
      "Stock Movement History",
      "Wastage & Adjustment Summary",
    ],
  },
  {
    id: "orders",
    title: "Order Reports",
    description:
      "Download order analytics broken down by status, date range, or a consolidated summary",
    icon: <ReceiptLongIcon sx={{ fontSize: 64 }} />,
    path: "/reports/orders",
    color: "#f59e0b",
    features: [
      "Orders by Status",
      "Orders by Date Range",
      "Order Summary",
      "PDF & CSV Export",
    ],
  },
  {
    id: "menu-items",
    title: "Menu Item Reports",
    description:
      "Analyse menu item performance, identify popular dishes, and track sales by item",
    icon: <RestaurantMenuIcon sx={{ fontSize: 64 }} />,
    path: "/reports/menu-items",
    color: "#8b5cf6",
    features: [
      "Popular Menu Items",
      "Sales by Menu Item",
      "Item Performance Summary",
      "PDF & CSV Export",
    ],
  },
];

const ReportsHub = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: { xs: "flex-start", md: "center" },
        p: { xs: 2, md: 2 },
        pt: { xs: 3, md: 2 },
        pb: { xs: 12, md: 2 },
      }}
    >
      {/* Page Header */}
      <Stack spacing={1} sx={{ mb: { xs: 3, md: 4 }, textAlign: "center", maxWidth: 800 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.9rem", md: "3rem" },
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-0.5px",
          }}
        >
          Reports
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "1rem",
            lineHeight: 1.6,
          }}
        >
          Select a report category to view detailed insights for your restaurant
        </Typography>
      </Stack>

      {/* Report Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
          },
          gap: { xs: 2, md: 3 },
          maxWidth: 900,
          width: "100%",
          gridAutoRows: { xs: "auto", sm: "320px" },
        }}
      >
        {reportCards.map((card) => (
          <ButtonBase
            key={card.id}
            onClick={() => navigate(card.path)}
            sx={{
              borderRadius: "20px",
              textAlign: "left",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              "&:hover": { transform: "translateY(-8px)" },
            }}
          >
            <Card
              elevation={0}
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "20px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"
                  }`,
                background: isDark
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(255,255,255,0.6)",
                backdropFilter: "blur(10px)",
                overflow: "hidden",
                position: "relative",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  border: `1px solid ${alpha(card.color, 0.5)}`,
                  boxShadow: `0 20px 60px ${alpha(card.color, 0.15)}`,
                  background: isDark
                    ? alpha(card.color, 0.05)
                    : alpha(card.color, 0.03),
                  "& .icon-box": {
                    transform: "scale(1.1) rotate(5deg)",
                    background: `linear-gradient(135deg, ${alpha(card.color, 0.2)}, ${alpha(card.color, 0.1)})`,
                  },
                  "& .feature-item": { transform: "translateX(4px)" },
                },
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 2.5, md: 3.5 },
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Stack spacing={2.5} sx={{ flex: 1 }}>
                  <Box
                    sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}
                  >
                    <Box
                      className="icon-box"
                      sx={{
                        width: 80,
                        height: 80,
                        flexShrink: 0,
                        borderRadius: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: `linear-gradient(135deg, ${alpha(card.color, 0.15)}, ${alpha(card.color, 0.05)})`,
                        color: card.color,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          letterSpacing: "-0.3px",
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.5,
                          fontSize: "0.85rem",
                        }}
                      >
                        {card.description}
                      </Typography>
                    </Stack>
                  </Box>

                  <Stack spacing={1.2}>
                    {card.features.map((feature, index) => (
                      <Box
                        key={index}
                        className="feature-item"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.2,
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <Box
                          sx={{
                            width: 5,
                            height: 5,
                            borderRadius: "50%",
                            background: card.color,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            fontSize: "0.8rem",
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </CardContent>

              {/* Decorative Corner */}
              <Box
                sx={{
                  position: "absolute",
                  top: -20,
                  right: -20,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(card.color, 0.1)}, transparent)`,
                  pointerEvents: "none",
                }}
              />
            </Card>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
};

export default ReportsHub;
